import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { z } from "zod";
import { MaterialKindSchema } from "../../../skills/leadcue-reply/scripts/lib/materials.ts";
import { replyFileSizeLabel } from "../src/reply-attachment-policy.ts";
import { NO_MATERIALS, type SendStripMaterials } from "../src/send-approve.ts";
import { AttachmentIcon } from "./icons.tsx";
import {
  fileSize,
  MATERIAL_FILE_ACCEPT,
  MaterialFileSchema,
  refusalOf,
  uploadMaterialFile,
} from "./material-request-sheet.tsx";
import { usePlanApi, useWorkspace, workspaceApi } from "./workspace-context.tsx";

/*
 * The file a draft says is attached (`docs/2026-10-02-materials-and-references.md`, "The draft's
 * state: awaiting a file"). The readiness is the server's, computed from the draft's requirement and
 * the campaign's material row; this strip only says it and offers the one thing that moves it on:
 * download the file to attach, upload the file the draft is waiting for, or redraft onto a file that
 * replaced the one the draft was written with. While a draft is not ready the page disables Approve
 * and the send controls; the server refuses them anyway (409 `material_not_ready`).
 */

const RequirementSchema = z.object({
  materialId: z.string(),
  campaignId: z.string(),
  kind: MaterialKindSchema,
  label: z.string(),
  file: MaterialFileSchema.nullable(),
  currentFile: MaterialFileSchema.nullable(),
});
type Requirement = z.infer<typeof RequirementSchema>;
const DraftMaterialsSchema = z.object({
  state: z.enum(["none", "ready", "awaiting_file", "replaced", "setting_removed"]),
  requirements: z.array(RequirementSchema),
  /** The campaign links this draft shares that a rep has since removed; an older server sends none. */
  removedLinks: z
    .array(z.object({ materialId: z.string(), kind: MaterialKindSchema, label: z.string() }))
    .default([]),
  /** The files the rep put on this revision; an older server sends none. */
  repFiles: z
    .array(z.object({ id: z.uuid(), fileName: z.string(), bytes: z.number().int().positive() }))
    .default([]),
});
export type DraftMaterialsView = z.infer<typeof DraftMaterialsSchema>;

const draftMaterialsKey = (planId: string, revision: number) =>
  ["draft-materials", planId, revision] as const;

/** A server without the route (the switch off, or an older build) answers 404: nothing to show. */
async function readDraftMaterials(
  planApi: string,
  revision: number,
): Promise<DraftMaterialsView | null> {
  const response = await fetch(`${planApi}/drafts/${revision}/materials`, {
    credentials: "same-origin",
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`draft_materials_${response.status}`);
  return DraftMaterialsSchema.parse(await response.json());
}

const notReady = (view: DraftMaterialsView | null | undefined): boolean =>
  view?.state === "awaiting_file" ||
  view?.state === "replaced" ||
  view?.state === "setting_removed";

/**
 * Whether a draft revision's approval is blocked by its file, for every reply step on the page at
 * once. It reads the same cache as the strip, so the page and the strip never disagree. Until the
 * read settles nothing is blocked: the server is the gate, and a flash of a disabled Approve on
 * every conversation would teach reps to ignore it.
 */
export function useDraftMaterialsBlocked(
  planId: string,
  revisions: readonly number[],
): (revision: number) => boolean {
  const planApi = usePlanApi(planId);
  const unique = [...new Set(revisions)];
  const results = useQueries({
    queries: unique.map((revision) => ({
      queryKey: draftMaterialsKey(planId, revision),
      queryFn: () => readDraftMaterials(planApi, revision),
    })),
  });
  return (revision: number) => {
    const index = unique.indexOf(revision);
    return index >= 0 && notReady(results[index]?.data);
  };
}

/**
 * The draft's files for the send controls, from the same cache as the strip: `"checking"` until the
 * read settles, so a send is never confirmed against files nobody has seen yet. A server without the
 * route answers 404, which is a draft with no file.
 */
export function useDraftMaterialsForSend(planId: string, revision: number): SendStripMaterials {
  const planApi = usePlanApi(planId);
  const query = useQuery({
    queryKey: draftMaterialsKey(planId, revision),
    queryFn: () => readDraftMaterials(planApi, revision),
  });
  if (query.data === undefined) return "checking";
  return query.data ?? NO_MATERIALS;
}

/** One requirement's own state: the aggregate says whether to block, this says what to show. */
function requirementState(requirement: Requirement): "ready" | "awaiting_file" | "replaced" {
  if (requirement.currentFile === null) return "awaiting_file";
  if (requirement.file !== null && requirement.file.sha256 !== requirement.currentFile.sha256)
    return "replaced";
  return "ready";
}

const downloadPath = (workspaceId: string, requirement: Requirement): string =>
  `${workspaceApi(workspaceId)}/campaigns/${encodeURIComponent(requirement.campaignId)}/materials/${encodeURIComponent(requirement.materialId)}/file`;

function DraftMaterialsStrip(props: {
  view: DraftMaterialsView;
  workspaceId: string;
  /** Where one of the rep's own files on this revision downloads from. */
  repFileHref: (fileId: string) => string;
  busy: string | null;
  error: string | null;
  onUpload: (requirement: Requirement, file: File) => void;
  onRedraft: () => void;
}) {
  const pickers = useRef<Record<string, HTMLInputElement | null>>({});
  const requirements = props.view.state === "none" ? [] : props.view.requirements;
  if (
    requirements.length === 0 &&
    props.view.repFiles.length === 0 &&
    props.view.removedLinks.length === 0
  )
    return null;
  return (
    <div className="lcr-draft-files">
      {props.view.repFiles.map((file) => (
        <div key={file.id} className="lcr-draft-file" data-state="ready">
          <AttachmentIcon size={18} className="lcr-draft-file-icon" />
          <p className="lcr-draft-file-text">
            Goes with this reply: <span className="lcr-draft-file-name">{file.fileName}</span>{" "}
            {/* In Bison's units, as the reply box shows the same file. */}
            <span className="lcr-draft-file-size">({replyFileSizeLabel(file.bytes)})</span>
          </p>
          <a
            className="lcr-button lcr-draft-file-action"
            href={props.repFileHref(file.id)}
            download={file.fileName}
          >
            Download
          </a>
        </div>
      ))}
      {props.view.removedLinks.map((link) => (
        <div key={link.materialId} className="lcr-draft-file" data-state="replaced">
          <AttachmentIcon size={18} className="lcr-draft-file-icon" />
          <p className="lcr-draft-file-text">
            <strong>The {link.label} link in this draft was removed from the campaign.</strong>
            <span className="lcr-draft-file-note">
              Redraft to write the reply without it, or take the link out of the draft.
            </span>
          </p>
          <button
            type="button"
            className="lcr-primary lcr-draft-file-action"
            disabled={props.busy !== null}
            onClick={props.onRedraft}
          >
            {props.busy === "redraft" ? "Redrafting…" : "Redraft"}
          </button>
        </div>
      ))}
      {requirements.map((requirement) => {
        const state = requirementState(requirement);
        const busy = props.busy === requirement.materialId;
        return (
          <div key={requirement.materialId} className="lcr-draft-file" data-state={state}>
            <AttachmentIcon size={18} className="lcr-draft-file-icon" />
            {state === "ready" && requirement.currentFile && (
              <>
                <p className="lcr-draft-file-text">
                  Attach this file when you send:{" "}
                  <span className="lcr-draft-file-name">{requirement.currentFile.fileName}</span>{" "}
                  <span className="lcr-draft-file-size">
                    ({fileSize(requirement.currentFile.bytes)})
                  </span>
                </p>
                <a
                  className="lcr-button lcr-draft-file-action"
                  href={downloadPath(props.workspaceId, requirement)}
                  download={requirement.currentFile.fileName}
                >
                  Download
                </a>
              </>
            )}
            {state === "awaiting_file" && (
              <>
                <p className="lcr-draft-file-text">
                  <strong>Upload the {requirement.label} before approving</strong>
                </p>
                <input
                  ref={(node) => {
                    pickers.current[requirement.materialId] = node;
                  }}
                  type="file"
                  accept={MATERIAL_FILE_ACCEPT}
                  className="lcr-visually-hidden"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(event) => {
                    const picked = event.target.files?.[0];
                    event.target.value = "";
                    if (picked) props.onUpload(requirement, picked);
                  }}
                />
                <button
                  type="button"
                  className="lcr-primary lcr-draft-file-action"
                  disabled={props.busy !== null}
                  onClick={() => pickers.current[requirement.materialId]?.click()}
                >
                  {busy ? "Uploading…" : "Upload"}
                </button>
              </>
            )}
            {state === "replaced" && (
              <>
                <p className="lcr-draft-file-text">
                  <strong>
                    The {requirement.label} was replaced after this draft was written.
                  </strong>
                  <span className="lcr-draft-file-note">Redraft to use the new file.</span>
                </p>
                <button
                  type="button"
                  className="lcr-primary lcr-draft-file-action"
                  disabled={props.busy !== null}
                  onClick={props.onRedraft}
                >
                  {props.busy === "redraft" ? "Redrafting…" : "Redraft"}
                </button>
              </>
            )}
          </div>
        );
      })}
      {props.error && (
        <p className="lcr-material-problem" role="alert">
          {props.error}
        </p>
      )}
    </div>
  );
}

function redraftRefusal(code: string | null): string {
  switch (code) {
    case "run_in_flight":
      return "A draft for this reply is already being prepared.";
    case "plan_changed":
    case "current_turn_event_not_reopenable":
      return "The lead wrote again, so this reply can no longer be redrafted. Answer the newest message.";
    default:
      return `The reply could not be redrafted (${code ?? "error"}). Nothing changed.`;
  }
}

/** The strip with its data, for one reply step's draft revision. */
export function DraftMaterials(props: {
  planId: string;
  draftRevision: number;
  onChange: () => Promise<void>;
}) {
  const planApi = usePlanApi(props.planId);
  const workspace = useWorkspace();
  const cache = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = draftMaterialsKey(props.planId, props.draftRevision);
  const view = useQuery({
    queryKey: key,
    queryFn: () => readDraftMaterials(planApi, props.draftRevision),
  });
  if (!view.data) return null;
  return (
    <DraftMaterialsStrip
      view={view.data}
      workspaceId={workspace.id}
      repFileHref={(fileId) =>
        `${planApi}/drafts/${props.draftRevision}/files/${encodeURIComponent(fileId)}`
      }
      busy={busy}
      error={error}
      onUpload={(requirement, file) => {
        setBusy(requirement.materialId);
        setError(null);
        void (async () => {
          try {
            const uploaded = await uploadMaterialFile(workspace.id, requirement.campaignId, file);
            const response = await fetch(
              `${workspaceApi(workspace.id)}/campaigns/${encodeURIComponent(requirement.campaignId)}/materials/${encodeURIComponent(requirement.materialId)}/fill`,
              {
                method: "POST",
                credentials: "same-origin",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ fileId: uploaded.fileId }),
              },
            );
            if (!response.ok) {
              const code = await refusalOf(response);
              throw new Error(
                code === "material_already_has_file"
                  ? `The ${requirement.label} was already uploaded. Refresh to see it.`
                  : `The file could not be saved (${code ?? "error"}). Upload it again.`,
              );
            }
            await cache.invalidateQueries({ queryKey: ["draft-materials", props.planId] });
            await cache.invalidateQueries({ queryKey: ["campaign-materials"] });
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Could not upload that file.");
          } finally {
            setBusy(null);
          }
        })();
      }}
      onRedraft={() => {
        setBusy("redraft");
        setError(null);
        void (async () => {
          try {
            const response = await fetch(`${planApi}/redraft-materials`, {
              method: "POST",
              credentials: "same-origin",
            });
            if (!response.ok) throw new Error(redraftRefusal(await refusalOf(response)));
            await props.onChange();
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : "The reply could not be redrafted.");
          } finally {
            setBusy(null);
          }
        })();
      }}
    />
  );
}
